import React, { useReducer, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, CheckCircle, AlertCircle, Building, Users, Award, Globe, FileText, CheckSquare } from 'lucide-react';
import { SectionTitle, Row, Col, FormField, Textarea, Input, Checkbox, Select, FileInput } from '../../../SharedInputs';

export function SectionExperience({ state, dispatch, errors }) {
  const updateField = (field, value) => {
    dispatch({ type: 'UPDATE_FIELD', section: 'experience', field, value });
  };
  const updateTrainingExperience = (file) => {
    dispatch({
      type: 'SET_FILE',
      section: 'experience',
      field: 'trainingCertificate',
      file
    });
  };

  return (
    <div>
      <SectionTitle icon={Award}>Experience & Operations</SectionTitle>
      
      <Row>
        <Col span={4}>
          <FormField label="Years in Business" required error={errors.find(e => e.includes('Years in business'))}>
            <Input
              type="number"
              value={state.experience.yearsInBusiness}
              onChange={(e) => updateField('yearsInBusiness', e.target.value)}
              placeholder="e.g., 5"
            />
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Number of Staff" required error={errors.find(e => e.includes('Number of staff'))}>
            <Input
              type="number"
              value={state.experience.numberOfStaff}
              onChange={(e) => updateField('numberOfStaff', e.target.value)}
              placeholder="e.g., 10"
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Have your company's staff completed any of the following training programs?">
            <Checkbox
              checked={state.experience.aeiTraining}
              onChange={(e) => updateField('aeiTraining', e.target.checked)}
            >
              AEI Education Agent Training Course (EATC) – PIER Online
            </Checkbox>
            <Checkbox
              checked={state.experience.isanaTraining}
              onChange={(e) => updateField('isanaTraining', e.target.checked)}
            >
              ISANA National Code Tutorial
            </Checkbox>
            <Checkbox
              checked={state.experience.maraTraining}
              onChange={(e) => updateField('maraTraining', e.target.checked)}
            >
              MARA (Migration Agent Registration Authority) Certificate
            </Checkbox>
          </FormField>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <FormField 
            label="Upload Training Certificate" 
            hint="Accepted formats: PDF, JPG, PNG (Max 2MB)"
            required
          >
            <FileInput
              accept=".pdf,.jpg,.jpeg,.png"
              maxSize={2 * 1024 * 1024} // 2 MB
              value={state.experience.trainingCertificate}
              onChange={updateTrainingExperience}
              placeholder="Upload a scanned copy of your staff training certificate"
              required
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={6}>
          <FormField label="Does your company provides Student Visa Assistance?">
            <Select
              value={state.experience.providesVisaAssistance}
              onChange={(e) => updateField('providesVisaAssistance', e.target.value === 'true')}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          </FormField>
        </Col>
        <Col span={6}>
          <FormField label="Does your company provides Travel Assistance?">
            <Select
              value={state.experience.providesTravelAssistance}
              onChange={(e) => updateField('providesTravelAssistance', e.target.value === 'true')}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={6}>
          <FormField label="VET Providers Represented">
            <Input
              type="number"
              value={state.experience.vetProviders}
              onChange={(e) => updateField('vetProviders', e.target.value)}
              placeholder="Number of VET providers"
            />
          </FormField>
        </Col>
        <Col span={6}>
          <FormField label="Higher Education Providers">
            <Input
              type="number"
              value={state.experience.higherProviders}
              onChange={(e) => updateField('higherProviders', e.target.value)}
              placeholder="Number of higher ed providers"
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={6}>
          <FormField label="Do you provide formal agreements with Australian Institutes?">
            <Select
              value={state.experience.hasFormalAgreements}
              onChange={(e) => updateField('hasFormalAgreements', e.target.value === 'true')}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          </FormField>
        </Col>
        {state.experience.hasFormalAgreements && (
          <Col span={6}>
            <FormField label="Provide Details">
              <Input
                value={state.experience.formalAgreementsDetails}
                onChange={(e) => updateField('formalAgreementsDetails', e.target.value)}
                placeholder="List institutes"
              />
            </FormField>
          </Col>
        )}
      </Row>

      <Row>
        <Col span={6}>
          <FormField label="Is your company affiliated with Sub-Agents or any other businesses?">
            <Select
              value={state.experience.hasSubAgents}
              onChange={(e) => updateField('hasSubAgents', e.target.value === 'true')}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          </FormField>
        </Col>
        {state.experience.hasSubAgents && (
          <Col span={6}>
            <FormField label="Provide Details">
              <Input
                value={state.experience.subAgentsDetails}
                onChange={(e) => updateField('subAgentsDetails', e.target.value)}
                placeholder="List sub-agents"
              />
            </FormField>
          </Col>
        )}
      </Row>

      <SectionTitle>Student Referrals (Past 3 Years)</SectionTitle>
      <p>Number of Students referred to Australian education Institutes.</p> <br/>
      <Row>
        <Col span={3}>
          <FormField label="High School">
            <Input
              type="number"
              value={state.experience.highSchoolStudents}
              onChange={(e) => updateField('highSchoolStudents', e.target.value)}
              placeholder="0"
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="RTO/College">
            <Input
              type="number"
              value={state.experience.rtoStudents}
              onChange={(e) => updateField('rtoStudents', e.target.value)}
              placeholder="0"
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="University">
            <Input
              type="number"
              value={state.experience.universityStudents}
              onChange={(e) => updateField('universityStudents', e.target.value)}
              placeholder="0"
            />
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="ELICOS">
            <Input
              type="number"
              value={state.experience.elicosStudents}
              onChange={(e) => updateField('elicosStudents', e.target.value)}
              placeholder="0"
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Fees Charges to Students" required error={errors.find(e => e.includes('Student fees'))}>
            <Textarea
              value={state.experience.studentFees}
              onChange={(e) => updateField('studentFees', e.target.value)}
              placeholder="Describe your fee structure..."
              rows={3}
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Marketing Channels Used for Recruiting Students" required error={errors.find(e => e.includes('Marketing channels'))}>
            <Textarea
              value={state.experience.marketingChannels}
              onChange={(e) => updateField('marketingChannels', e.target.value)}
              placeholder="List your marketing channels (e.g., social media, education fairs, partnerships)..."
              rows={3}
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Popular Courses you are Promoting">
            <Textarea
              value={state.experience.popularCourses}
              onChange={(e) => updateField('popularCourses', e.target.value)}
              placeholder="List the courses you frequently promote..."
              rows={3}
            />
          </FormField>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Target Countries you wish to Recruit for CIET" required error={errors.find(e => e.includes('Target countries'))}>
            <Textarea
              value={state.experience.targetCountries}
              onChange={(e) => updateField('targetCountries', e.target.value)}
              placeholder="List countries where you wish to recruit students on behalf of CIET..."
              rows={3}
            />
          </FormField>
        </Col>
      </Row>
    </div>
  );
}